import { useState, useEffect } from 'react';

/**
 * Custom hook to track media query matches
 * @param query - Media query string (e.g., '(max-width: 768px)')
 * @returns boolean indicating if the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add listener for future changes
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
};

/**
 * Predefined breakpoint hooks
 * Mobile: 0 - 799px
 * Tablet: 800px - 1023px
 * Desktop: 1024px - 1439px
 * Large Desktop: 1440px+
 */
export const useIsMobile = () => useMediaQuery('(max-width: 799px)');
export const useIsTablet = () => useMediaQuery('(min-width: 800px) and (max-width: 1023px)');
export const useIsDesktop = () => useMediaQuery('(min-width: 1024px) and (max-width: 1439px)');
export const useIsLargeDesktop = () => useMediaQuery('(min-width: 1440px)');

/**
 * Get current breakpoint name
 * @returns 'mobile' | 'tablet' | 'desktop' | 'largeDesktop'
 */
export const useBreakpoint = (): 'mobile' | 'tablet' | 'desktop' | 'largeDesktop' => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const isLargeDesktop = useIsLargeDesktop();

  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';
  if (isDesktop) return 'desktop';
  if (isLargeDesktop) return 'largeDesktop';
  return 'desktop'; // default
};
